// @flow
import React, { Component } from 'react';
import Autosuggest from 'react-autosuggest';
import { asField } from 'informed';
import { throttle, debounce } from 'throttle-debounce';

import { SERVER_ADDRESS, fetchPost } from '../util/actionsUtil';

// Invisible Informed Text field controlled by the autosuggest field
const ControlledText = asField(({
  fieldState, fieldApi, ...props
}) => {
  const { setValue, setTouched } = fieldApi;
  const {
    onChange, onBlur, forwardedRef, storeFieldApi, initialValue,
  } = props;
  storeFieldApi(fieldApi);
  return (
    <input
      ref={forwardedRef}
      onChange={(e) => {
        setValue(e.target.value);
        if (onChange) {
          onChange(e);
        }
      }}
      onBlur={(e) => {
        setTouched(true);
        if (onBlur) {
          onBlur(e);
        }
      }}
      value={initialValue || ''}
      style={{ display: 'none' }}
    />
  );
});

/*
  Typeahead component for autofill fields.
  Asynchronously queries for results from API endpoint
  In general, API endpoints should search for distinct results for a given field,
  case insensitive substring match
  But theoretically endpoint can return an array of strings via any method

  *** Props ***
  field (required): same as informed fields
  endpoint (required): autofill API endpoint to hit
  initialValue: same as informed fields
  Other props will be passed into <input> element

*/

type Props = {
  field: string,
  endpoint: string,
  initialValue?: string,
  placeholder?: string,
}

type State = {
  suggestions: Object[],
  value: string,
}

const getSuggestionValue = suggestion => suggestion;
const renderSuggestion = suggestion => suggestion;

class Typeahead extends Component<Props, State> {
  fieldApi: Object

  constructor(props: Props) {
    super(props);
    this.state = {
      value: props.initialValue || '',
      suggestions: [],
    };
  }

  // Make API Call to get results, expected result is array of strings
  // Filtering done backend, display all returned results
  loadSuggestions(query: string) {
    fetchPost(`${SERVER_ADDRESS}/autofill/${this.props.endpoint}`,
      { query },
      // success
      (data) => {
        this.setState({
          suggestions: data.suggestions,
        });
      },
      // error
      () => {
        this.setState({
          suggestions: [],
        });
      });
  }

  loadSuggestionsThrottled = throttle(500, this.loadSuggestions);

  loadSuggestionsDebounced = debounce(500, this.loadSuggestions);

  onChange = (event: Object, { newValue }: Object) => {
    this.setState({
      value: newValue,
    });
    this.fieldApi.setValue(newValue);
    this.fieldApi.setTouched(true);
  };

  onSuggestionsFetchRequested = ({ value }: Object) => {
    if (value.length < 5) {
      this.loadSuggestionsThrottled(value);
    } else {
      this.loadSuggestionsDebounced(value);
    }
  };

  onSuggestionsClearRequested = () => {
    this.setState({
      suggestions: [],
    });
  };

  storeFieldApi = (fieldApi: Object) => {
    this.fieldApi = fieldApi;
  };

  render() {
    const { suggestions } = this.state;
    let { value } = this.state;
    const { field, initialValue, ...rest } = this.props;
    // override Autosuggest value if fieldApi used to change value
    if (this.fieldApi) {
      const fieldValue = this.fieldApi.getValue();
      if (fieldValue && value !== fieldValue) {
        value = fieldValue;
      }
      if (value && !fieldValue) {
        value = '';
      }
    }

    const inputProps = {
      value,
      onChange: this.onChange,
      ...rest,
    };

    return (
      <span>
        <ControlledText
          field={field}
          initialValue={initialValue}
          storeFieldApi={this.storeFieldApi}
        />
        <Autosuggest
          suggestions={suggestions}
          onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
          onSuggestionsClearRequested={this.onSuggestionsClearRequested}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          inputProps={inputProps}
        />
      </span>
    );
  }
}

export default Typeahead;



// WEBPACK FOOTER //
// ./client/src/components/Typeahead.jsx